import { useState, useCallback, useMemo, useEffect, useRef, Fragment } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router';
import {
  CameraIcon,
  MapPinIcon,
  TagIcon,
  ClipboardListIcon,
  CheckCircle2Icon,
  CopyIcon,
  ChevronLeftIcon,
  Loader2Icon,
  ThumbsUpIcon,
  AlertCircleIcon,
  NavigationIcon,
  SparklesIcon,
  SearchIcon,
  SmartphoneIcon,
  LogInIcon,
  UserCheckIcon,
  CheckIcon,
  ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { appIdentity } from '@/app/app-config';
import { MapPinPicker } from '@/components/map/map-pin-picker';
import type { PhotoLocation } from '@/components/map/use-photo-markers';
import { AddressSearchInput } from '@/components/report/address-search-input';
import { formatDistance, formatRelativeTime, getStatusConfig, SEVERITY_LABELS } from '@/lib/report-helpers';
import { useQuery } from '@tanstack/react-query';
import {
  useGetCivicCategories,
  useCreateReport,
  useUpvoteReport,
  useClaimReport,
  getGetNearbyReportsQueryOptions,
  getMatchNearestAssetQueryOptions,
  type Category,
  type NearbyReport,
  type CreateReportRequestSeverity,
  type DetectionResult,
  type MatchedAsset
} from '@cdf/api-client-react';
import { useAuth } from '@/hooks/use-auth';
import { useHuntChallenges } from '@/hooks/use-hunt-challenges';
import { useMultiPhoto, MAX_PHOTOS } from '@/hooks/use-multi-photo';
import { PhotoMetadataCard, PhotoThumbnail } from '@/components/report/photo-metadata-card';

type Step = 'photo' | 'location' | 'category' | 'deflection' | 'details' | 'success';

type ClaimStatus =
  | 'idle'            // no claim activity
  | 'pending-claim'   // sign-in redirect in progress or waiting for auth to settle
  | 'claiming'        // claim API call in progress
  | 'claimed'         // newly claimed after sign-in
  | 'already-owned'   // was already owned by the same user (idempotent)
  | 'error';          // claim failed — fall back to tracking link

const PENDING_CLAIM_KEY = 'cdf:pendingReportClaim';

const STEPS: { id: Step; label: string; icon: typeof CameraIcon }[] = [
  { id: 'photo', label: 'Photo', icon: CameraIcon },
  { id: 'location', label: 'Location', icon: MapPinIcon },
  { id: 'category', label: 'Category', icon: TagIcon },
  { id: 'deflection', label: 'Check', icon: AlertCircleIcon },
  { id: 'details', label: 'Details', icon: ClipboardListIcon },
  { id: 'success', label: 'Done', icon: CheckCircle2Icon }
];

// Purpose-scoped category catalogue (matches civic API + purposes.ts slugs)
const REPORT_PURPOSES = [
  { slug: 'councils', label: 'Council' },
  { slug: 'farms', label: 'Farm' },
  { slug: 'workplace-safety', label: 'OH&S' },
  { slug: 'facilities', label: 'Facilities' },
  { slug: 'homes-rentals', label: 'Home' }
] as const;

type ReportPurposeSlug = (typeof REPORT_PURPOSES)[number]['slug'] | 'treasure-hunt' | 'event';

function parseReportPurpose(raw: string | null): ReportPurposeSlug {
  const allowed = new Set<string>([
    ...REPORT_PURPOSES.map((p) => p.slug),
    'treasure-hunt',
    'event'
  ]);
  if (raw && allowed.has(raw)) return raw as ReportPurposeSlug;
  return 'councils';
}

export function ReportWizard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const purpose = parseReportPurpose(searchParams.get('purpose'));
  const eventSlug = purpose === 'event' ? searchParams.get('event')?.trim() || undefined : undefined;
  const eventName = purpose === 'event' ? searchParams.get('eventName')?.trim() || undefined : undefined;
  const purposeLabel =
    REPORT_PURPOSES.find((p) => p.slug === purpose)?.label ??
    (purpose === 'treasure-hunt' ? 'Hunt demo' : purpose === 'event' ? eventName ?? 'Event' : 'Council');

  const [step, setStep] = useState<Step>('photo');
  // Multi-photo management: up to 5 photos per report with EXIF metadata.
  const multiPhoto = useMultiPhoto();
  const { photos, activePhotoId, setActivePhotoId, blobKeys, hasUploadedPhoto, isUploading } = multiPhoto;
  // Build photo location markers for the map from uploaded photos with GPS metadata.
  const photoLocations: PhotoLocation[] = useMemo(
    () =>
      photos
        .filter((p) => p.metadata?.hasGps && p.metadata.latitude != null && p.metadata.longitude != null)
        .map((p, i) => ({
          id: p.id,
          url: p.preview,
          latitude: p.metadata!.latitude!,
          longitude: p.metadata!.longitude!,
          label: `Photo ${i + 1}`,
          timestamp: p.metadata!.dateTimeOriginal ?? undefined,
        })),
    [photos],
  );
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [addressText, setAddressText] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<CreateReportRequestSeverity>('medium');
  const [reporterName, setReporterName] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [reporterPhone, setReporterPhone] = useState('');
  const [contactPrivacy, setContactPrivacy] = useState(true);
  const [trackingUrl, setTrackingUrl] = useState<string | null>(null);
  const [trackingToken, setTrackingToken] = useState<string | null>(null);
  const [attachedAtSubmit, setAttachedAtSubmit] = useState(false);
  const [claimStatus, setClaimStatus] = useState<ClaimStatus>('idle');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [gpsPermissionDenied, setGpsPermissionDenied] = useState(false);
  const autoGpsPromptedRef = useRef(false);
  const hasCoordsRef = useRef(false);
  const [copied, setCopied] = useState(false);
  const trackingLinkInputRef = useRef<HTMLInputElement>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Track which photo's GPS was accepted to populate the report location.
  const [acceptedLocationPhotoId, setAcceptedLocationPhotoId] = useState<string | null>(null);
  const [detection, setDetection] = useState<DetectionResult | null>(null);
  const [matchedAsset, setMatchedAsset] = useState<MatchedAsset | null>(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [aiAutoSelected, setAiAutoSelected] = useState(false);
  const [customTypeLabel, setCustomTypeLabel] = useState<string | null>(null);
  const [customLabelInput, setCustomLabelInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customLabelError, setCustomLabelError] = useState<string | null>(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  // ── Auth + claim hooks ───────────────────────────────────────────
  const { enabled: authEnabled, ready: authReady, isAuthenticated, login } = useAuth();
  const claimMutation = useClaimReport();

  // ── Hunt challenge hook ──────────────────────────────────────────
  const { incompleteByType, completeChallenge } = useHuntChallenges();
  const demoChallenge = incompleteByType.demo_report;
  const isDemoMode = !!demoChallenge;

  // Prefill location from URL params (e.g. from AR 'Report here' link)
  const [prefilledAssetId, setPrefilledAssetId] = useState<string | null>(null);

  // ── Restore wizard state after Entra sign-in redirect ─────────────
  // When the user clicks "Sign in" on the success step, we persist the
  // tracking token to sessionStorage before the redirect. On remount we
  // restore the success step so the claim effect can fire.
  useEffect(() => {
    const pending = sessionStorage.getItem(PENDING_CLAIM_KEY);
    if (!pending) return;
    try {
      const { trackingToken: token, trackingUrl: url } = JSON.parse(pending);
      if (token && url) {
        setTrackingToken(token);
        setTrackingUrl(url);
        setStep('success');
        setClaimStatus('pending-claim');
        // Replace history so back from success exits the wizard
        navigate(`${window.location.pathname}${window.location.search}`, { replace: true, state: { wizardStep: 'success', terminal: true } });
      }
    } catch {
      sessionStorage.removeItem(PENDING_CLAIM_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Claim the report after sign-in completes ──────────────────────
  useEffect(() => {
    if (!authEnabled || !authReady || !isAuthenticated) return;
    const raw = sessionStorage.getItem(PENDING_CLAIM_KEY);
    if (!raw) return;
    if (claimStatus === 'claiming' || claimStatus === 'claimed' || claimStatus === 'already-owned') return;

    let token: string | null = null;
    try {
      token = (JSON.parse(raw) as { trackingToken?: string }).trackingToken ?? null;
    } catch {
      sessionStorage.removeItem(PENDING_CLAIM_KEY);
      return;
    }
    if (!token) return;

    setClaimStatus('claiming');
    claimMutation.mutate(
      { token },
      {
        onSuccess: (res) => {
          sessionStorage.removeItem(PENDING_CLAIM_KEY);
          setClaimStatus(res.alreadyOwned ? 'already-owned' : 'claimed');
        },
        onError: () => {
          sessionStorage.removeItem(PENDING_CLAIM_KEY);
          setClaimStatus('error');
        }
      }
    );
  }, [authEnabled, authReady, isAuthenticated, claimStatus, claimMutation]);

  useEffect(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const assetId = searchParams.get('assetId');
    if (lat && lng) {
      setLatitude(parseFloat(lat));
      setLongitude(parseFloat(lng));
    }
    if (assetId) {
      setPrefilledAssetId(assetId);
    }
  }, [searchParams]);

  // ── Initialize browser history for wizard step tracking ───────────
  // Seed the initial history entry with {wizardStep:'photo'} so that
  // browser back from the first forward step returns to photo instead
  // of leaving the wizard. Skipped when a pending claim will restore
  // to success (the claim-restore effect handles the terminal replace).
  useEffect(() => {
    const pending = sessionStorage.getItem(PENDING_CLAIM_KEY);
    if (!pending) {
      navigate(`${window.location.pathname}${window.location.search}`, { replace: true, state: { wizardStep: 'photo' } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data hooks ──────────────────────────────────────────────────

  const { data: categoriesData } = useGetCivicCategories({
    purpose: purpose === 'event' ? undefined : purpose,
  });

  // Sync the first photo's AI detection result into wizard-level state.
  // This drives category auto-selection and severity prefill downstream.
  useEffect(() => {
    const firstWithDetection = photos.find((p) => p.detection);
    setDetection(firstWithDetection?.detection ?? null);
  }, [photos]);

  const createMutation = useCreateReport();

  const { data: nearbyData, isLoading: nearbyLoading } = useQuery({
    ...getGetNearbyReportsQueryOptions(
      { lat: latitude ?? 0, lng: longitude ?? 0, categoryId: categoryId ?? '', radius: 150 }
    ),
    enabled: step === 'deflection' && !!latitude && !!longitude && !!categoryId
  });

  const upvoteMutation = useUpvoteReport();

  // Match nearest seeded asset when we have detection + location
  const { data: matchData } = useQuery({
    ...getMatchNearestAssetQueryOptions(
      { assetTypeSlug: detection?.assetTypeSlug ?? '', lat: latitude ?? 0, lng: longitude ?? 0 }
    ),
    enabled: !!detection?.assetTypeSlug && !!latitude && !!longitude && (step === 'category' || step === 'deflection' || step === 'details')
  });

  useEffect(() => {
    if (matchData?.matchedAsset) {
      setMatchedAsset(matchData.matchedAsset);
    }
  }, [matchData]);

  // Prefill severity from AI detection
  useEffect(() => {
    if (detection?.severity) {
      setSeverity(detection.severity);
    }
  }, [detection]);

  const categories = useMemo(() => categoriesData?.categories ?? [], [categoriesData]);
  const nearbyReports = useMemo(() => nearbyData?.reports ?? [], [nearbyData]);

  // Auto-select AI-detected category only when it exists in this purpose's list
  useEffect(() => {
    if (
      step === 'category' &&
      !categoryId &&
      detection?.categoryId &&
      (detection.confidence ?? 0) >= 0.7 &&
      categories.some((c) => c.id === detection.categoryId)
    ) {
      setCategoryId(detection.categoryId);
      setAiAutoSelected(true);
    }
  }, [step, categoryId, detection, categories]);

  // Clear selected category when purpose changes so the user re-picks from the new list
  useEffect(() => {
    setCategoryId(null);
    setAiAutoSelected(false);
    setCategorySearch('');
  }, [purpose]);

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    const q = categorySearch.toLowerCase();
    return categories.filter(
      (cat) =>
        (cat.name ?? '').toLowerCase().includes(q) ||
        (cat.assetTypeName?.toLowerCase().includes(q) ?? false)
    );
  }, [categories, categorySearch]);

  function setPurpose(next: ReportPurposeSlug) {
    const params = new URLSearchParams(searchParams);
    params.set('purpose', next);
    setSearchParams(params, { replace: true });
  }

  // ── Handlers ────────────────────────────────────────────────────

  const handleAcceptPhotoLocation = useCallback(
    (photoId: string) => {
      const coords = multiPhoto.acceptPhotoLocation(photoId);
      if (coords) {
        setLatitude(coords.latitude);
        setLongitude(coords.longitude);
        setAcceptedLocationPhotoId(photoId);
      }
    },
    [multiPhoto]
  );

  const handleGps = useCallback(() => {
    setGpsError(null);
    setGpsPermissionDenied(false);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGpsError("Your browser doesn't support location — drop the pin manually.");
      return;
    }
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setGpsError('Location is only available on a secure (HTTPS) connection — drop the pin manually.');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
        setGpsLoading(false);
        setGpsError(null);
        setGpsPermissionDenied(false);
      },
      (err) => {
        setGpsLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGpsPermissionDenied(true);
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGpsError('Your location could not be determined — drop the pin manually.');
        } else if (err.code === err.TIMEOUT) {
          setGpsError('Finding your location took too long — try again or drop the pin manually.');
        } else {
          setGpsError('Could not get your location — drop the pin manually.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // ── Auto-prompt for location when the Location step opens ─────────
  // iOS Safari only shows the native permission prompt when
  // getCurrentPosition is actually called. We query the Permissions API
  // (iOS 16+) to learn the current state first, subscribe to changes so
  // the UI can react if the user grants mid-session, and then fire a
  // one-shot getCurrentPosition call so the prompt appears immediately on
  // step entry — unless permission is already denied or coords are set.
  // Browsers without Permissions API support still get the auto-prompt.
  useEffect(() => {
    if (step !== 'location') return;

    let cancelled = false;
    let status: PermissionStatus | null = null;

    const maybeAutoPrompt = () => {
      if (cancelled) return;
      if (autoGpsPromptedRef.current) return;
      if (latitude != null && longitude != null) return;
      if (typeof window !== 'undefined' && window.isSecureContext === false) return;
      if (typeof navigator === 'undefined' || !navigator.geolocation) return;
      autoGpsPromptedRef.current = true;
      handleGps();
    };

    try {
      if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
        navigator.permissions
          .query({ name: 'geolocation' as PermissionName })
          .then((s) => {
            if (cancelled) return;
            status = s;
            const state = s.state as 'granted' | 'prompt' | 'denied';
            if (state === 'denied') {
              setGpsPermissionDenied(true);
            }
            s.onchange = () => {
              const newState = s.state as 'granted' | 'prompt' | 'denied';
              if (newState === 'granted') {
                // User re-enabled location — clear denied state and auto-fetch.
                setGpsPermissionDenied(false);
                setGpsError(null);
                if (!hasCoordsRef.current) {
                  handleGps();
                }
              } else if (newState === 'denied') {
                setGpsPermissionDenied(true);
              }
            };
            // Skip the auto-prompt only when we KNOW permission is denied.
            if (state !== 'denied') {
              maybeAutoPrompt();
            }
          })
          .catch(() => {
            // Permissions API rejected — treat as unknown and prompt anyway.
            maybeAutoPrompt();
          });
      } else {
        // No Permissions API — prompt directly (older browsers / iOS < 16).
        maybeAutoPrompt();
      }
    } catch {
      // navigator.permissions threw — prompt directly.
      maybeAutoPrompt();
    }

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleLocationChange = useCallback((lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);
  }, []);

  // Keep a ref in sync so the permission-change listener can check
  // whether coordinates are already set without stale closures.
  useEffect(() => {
    hasCoordsRef.current = latitude != null && longitude != null;
  }, [latitude, longitude]);

  const handleSubmit = useCallback(() => {
    if (!latitude || !longitude || !categoryId) return;
    if (purpose === 'event' && !eventSlug) {
      setSubmitError('This event report link is missing its event. Return to the event and start the report again.');
      return;
    }
    setSubmitError(null);
    createMutation.mutate(
      {
        data: {
          photoBlobKeys: blobKeys,
          latitude,
          longitude,
          categoryId,
          addressText: addressText || undefined,
          description: description || undefined,
          reporterName: reporterName || undefined,
          reporterEmail: reporterEmail || undefined,
          reporterPhone: reporterPhone || undefined,
          contactPrivacy,
          severity,
          customTypeLabel: customTypeLabel ?? undefined,
          aiDetection: detection ? {
            assetType: detection.assetTypeSlug ?? undefined,
            problemType: detection.problemType ?? undefined,
            severity: detection.severity ?? undefined,
            description: detection.description ?? undefined,
            confidence: detection.confidence
          } : undefined,
          matchedAssetId: matchedAsset?.assetId ?? prefilledAssetId ?? undefined,
          eventSlug,
          eventName,
          isDemo: isDemoMode
        }
      },
      {
        onSuccess: (res) => {
          setTrackingUrl(res.trackingUrl);
          setTrackingToken(res.trackingToken);
          setAttachedAtSubmit(res.attachedToAccount);
          setStep('success');
          // Replace history so back from success exits the wizard
          // instead of returning to editable steps.
          navigate(`${window.location.pathname}${window.location.search}`, { replace: true, state: { wizardStep: 'success', terminal: true } });
          // Fire demo_report challenge completion with the report id as evidence
          if (demoChallenge && res.reportId) {
            completeChallenge(demoChallenge.id, { demoReportId: res.reportId });
          }
        },
        onError: (err: unknown) => {
          const msg = err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to submit report. Please try again.';
          setSubmitError(msg);
        }
      }
    );
  }, [blobKeys, latitude, longitude, categoryId, addressText, description, reporterName, reporterEmail, reporterPhone, contactPrivacy, severity, customTypeLabel, detection, matchedAsset, prefilledAssetId, eventSlug, eventName, purpose, createMutation, isDemoMode, demoChallenge, completeChallenge, navigate]);

  // ── Sign-in handler: persist pending claim, then redirect to Entra ──
  const handleSignInForClaim = useCallback(() => {
    if (!trackingToken || !trackingUrl) return;
    sessionStorage.setItem(
      PENDING_CLAIM_KEY,
      JSON.stringify({ trackingToken, trackingUrl })
    );
    setClaimStatus('pending-claim');
    login();
  }, [trackingToken, trackingUrl, login]);

  const handleUpvote = useCallback(
    (token: string) => {
      upvoteMutation.mutate(
        { token },
        { onSuccess: () => navigate(`/track/${token}`) }
      );
    },
    [upvoteMutation, navigate]
  );

  const handleCopyLink = useCallback(async () => {
    if (!trackingUrl) return;

    const directUrl = new URL(trackingUrl, window.location.origin).toString();
    let didCopy = false;

    try {
      await navigator.clipboard.writeText(directUrl);
      didCopy = true;
    } catch {
      // Safari and some embedded mobile browsers do not expose the Clipboard API.
      // Select the rendered field so the legacy, user-gesture copy command can run.
      const input = trackingLinkInputRef.current;
      if (input) {
        input.focus();
        input.select();
        input.setSelectionRange(0, input.value.length);
        didCopy = document.execCommand('copy');
      }
    }

    if (didCopy) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [trackingUrl]);

  const BrandIcon = appIdentity.icon;
  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const [furthestStepIndex, setFurthestStepIndex] = useState(0);

  const canEnterStep = useCallback((target: Step): boolean => {
    switch (target) {
      case 'photo':
      case 'location':
        return true;
      case 'category':
        return latitude != null && longitude != null;
      case 'deflection':
      case 'details':
        return latitude != null && longitude != null && !!categoryId;
      case 'success':
        return false; // only via submission or claim-restore
      default:
        return false;
    }
  }, [latitude, longitude, categoryId]);

  const goToStep = useCallback((target: Step) => {
    const targetIndex = STEPS.findIndex((s) => s.id === target);
    if (targetIndex < 0 || !canEnterStep(target)) return;
    // Push a history entry so browser back steps within the wizard
    navigate(`${window.location.pathname}${window.location.search}`, { state: { wizardStep: target } });
    setStep(target);
    setFurthestStepIndex((prev) => Math.max(prev, targetIndex));
  }, [canEnterStep, navigate]);

  const goBack = useCallback(() => {
    if (stepIndex > 0) {
      // Use browser history.back() so hardware/browser back and our
      // Back button share the same mechanism. The location-sync
      // effect below updates the step when popstate fires.
      navigate(-1);
    }
  }, [stepIndex, navigate]);

  // Clamp furthest reachable step when prerequisites change so forward
  // navigation stays consistent if a prerequisite is cleared.
  useEffect(() => {
    setFurthestStepIndex((prev) => {
      let maxReachable = 0;
      for (let i = 0; i < STEPS.length; i++) {
        if (canEnterStep(STEPS[i]!.id)) maxReachable = i;
        else break;
      }
      return Math.min(prev, maxReachable);
    });
  }, [canEnterStep]);

  // ── Sync step with browser history (back/forward) ─────────────────
  // When the user presses hardware/browser back or forward, React
  // Router updates `location` and this effect reads the step marker
  // from the history entry's state, routing it through the same
  // canEnterStep guard used by the in-page controls.
  useEffect(() => {
    const locState = location.state as { wizardStep?: Step; terminal?: boolean } | null;

    // Terminal success — stay on success, never return to editable steps
    if (locState?.terminal || locState?.wizardStep === 'success') {
      if (step !== 'success') setStep('success');
      return;
    }

    // If we're on success and browser back popped a non-terminal wizard
    // step, exit the wizard entirely (back from success → leave).
    if (step === 'success' && locState?.wizardStep) {
      navigate('/', { replace: true });
      return;
    }

    // Sync step from history state (browser back/forward re-entry)
    if (locState?.wizardStep) {
      if (canEnterStep(locState.wizardStep)) {
        setStep(locState.wizardStep);
      }
    }
    // If no wizardStep in state, the browser is exiting the wizard — let it
  }, [location, navigate, canEnterStep, step]);

  const hasReportProgress =
    step !== 'success' &&
    (stepIndex > 0 || hasUploadedPhoto || (latitude != null && longitude != null) || !!categoryId);

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (hasReportProgress) {
      e.preventDefault();
      setShowLeaveDialog(true);
    }
  };

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col bg-background">
      {/* Header */}
      <header className="bg-background/80 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40 flex items-center gap-2 border-b border-border px-4 py-3 backdrop-blur-md">
        <Link
          to="/"
          onClick={handleHomeClick}
          className="focus-visible:ring-ring flex min-w-0 items-center gap-1.5 rounded-md font-semibold focus-visible:ring-2 focus-visible:outline-none"
          aria-label={`${appIdentity.name} — home`}
        >
          <BrandIcon className="size-5 shrink-0 text-primary" aria-hidden />
          <span className="truncate text-sm tracking-tight">{appIdentity.name}</span>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          {stepIndex > 0 && step !== 'success' && (
            <Button variant="ghost" size="icon" onClick={goBack} className="h-8 w-8 shrink-0" aria-label="Go back">
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
          )}
          <h1 className="text-lg font-semibold">Report</h1>
        </div>
      </header>

      {/* Progress indicator — numbered step buttons */}
      {step !== 'success' && (
        <div className="px-4 py-1.5">
          <div className="flex items-center">
            {STEPS.map((s, i) => {
              if (s.id === 'success') return null;
              const isActive = step === s.id;
              const isVisited = i <= furthestStepIndex;
              const isCompleted = i < stepIndex;
              const canEnter = canEnterStep(s.id);
              return (
                <Fragment key={s.id}>
                  {i > 0 && (
                    <div
                      className={cn(
                        'h-0.5 min-w-[4px] flex-1 transition-colors',
                        i <= furthestStepIndex ? 'bg-primary' : 'bg-muted'
                      )}
                    />
                  )}
                  <button
                    type="button"
                    disabled={!canEnter}
                    onClick={() => goToStep(s.id)}
                    aria-label={`Go to ${s.label} step`}
                    aria-current={isActive ? 'step' : undefined}
                    className={cn(
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                      canEnter ? 'cursor-pointer' : 'cursor-not-allowed'
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-7 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                          : isCompleted
                            ? 'bg-primary text-primary-foreground'
                            : isVisited
                              ? 'bg-primary/80 text-primary-foreground'
                              : canEnter
                                ? 'border border-muted-foreground/30 bg-background text-muted-foreground'
                                : 'border border-muted/30 bg-muted/40 text-muted-foreground/40'
                      )}
                    >
                      {isCompleted ? <CheckIcon className="size-4" /> : i + 1}
                    </span>
                  </button>
                </Fragment>
              );
            })}
          </div>
          <p className="mt-0.5 text-center text-xs font-medium text-muted-foreground">
            Step {stepIndex + 1} of 5 — {STEPS[stepIndex]?.label}
          </p>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-4 py-4">
        {/* Hunt demo mode banner */}
        {isDemoMode && step !== 'success' && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
            <SparklesIcon className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Treasure Hunt demo mode — this report won't appear in real staff queues. Submit to earn <strong>+{demoChallenge?.points ?? 0} pts</strong>.
            </p>
          </div>
        )}
        {/* ── Step: Photo ── */}
        {step === 'photo' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-medium">Take or upload photos</h2>
              <p className="text-sm text-muted-foreground">
                A clear photo helps the responsible authority identify and fix the issue faster — but it's optional. You can attach up to {MAX_PHOTOS} photos and skip this step entirely.
              </p>
            </div>
            {/* Hidden file inputs — camera keeps `capture="environment"` so mobile
                browsers open the camera directly; gallery supports multi-select. */}
            <input
              ref={multiPhoto.cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={multiPhoto.handlePhotoSelect}
              onClick={(e) => {
                e.currentTarget.value = '';
              }}
            />
            <input
              ref={multiPhoto.galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={multiPhoto.handlePhotoSelect}
              onClick={(e) => {
                e.currentTarget.value = '';
              }}
            />

            {/* Photo gallery thumbnails */}
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {photos.map((photo, idx) => (
                  <PhotoThumbnail
                    key={photo.id}
                    preview={photo.preview}
                    index={idx}
                    active={activePhotoId === photo.id}
                    uploading={photo.uploading}
                    hasBlobKey={!!photo.blobKey}
                    hasGps={!!photo.metadata?.hasGps}
                    onClick={() => setActivePhotoId(photo.id)}
                    onRemove={() => multiPhoto.removePhoto(photo.id)}
                    onMoveLeft={() => multiPhoto.movePhoto(photo.id, 'left')}
                    onMoveRight={() => multiPhoto.movePhoto(photo.id, 'right')}
                    canMoveLeft={idx > 0}
                    canMoveRight={idx < photos.length - 1}
                  />
                ))}
                {multiPhoto.canAddMore && (
                  <button
                    type="button"
                    onClick={() => multiPhoto.cameraInputRef.current?.click()}
                    className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-muted-foreground/40"
                  >
                    <CameraIcon className="h-5 w-5" />
                    <span className="text-[10px]">Add</span>
                  </button>
                )}
              </div>
            )}

            {/* Active photo large preview + metadata card */}
            {activePhotoId && photos.find((p) => p.id === activePhotoId) && (
              (() => {
                const activePhoto = photos.find((p) => p.id === activePhotoId)!;
                return (
                  <div className="space-y-3">
                    <div className="relative overflow-hidden rounded-lg border border-border">
                      <img src={activePhoto.preview} alt="Photo preview" className="h-64 w-full object-cover" />
                      {activePhoto.uploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <Loader2Icon className="h-8 w-8 animate-spin text-white" />
                        </div>
                      )}
                      {activePhoto.blobKey && (
                        <div className="absolute right-2 top-2 rounded-full bg-green-500 p-1">
                          <CheckCircle2Icon className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                    {/* EXIF metadata confirmation card with "Accept photo location" CTA */}
                    <PhotoMetadataCard
                      metadata={activePhoto.metadata}
                      uploading={activePhoto.uploading}
                      error={activePhoto.error}
                      onAcceptLocation={() => handleAcceptPhotoLocation(activePhoto.id)}
                      locationAccepted={acceptedLocationPhotoId === activePhoto.id}
                    />
                    {/* Per-photo AI detection result */}
                    {activePhoto.blobKey && activePhoto.detecting && (
                      <div className="flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
                        <SparklesIcon className="h-4 w-4 animate-pulse" />
                        AI is analyzing this photo…
                      </div>
                    )}
                    {activePhoto.blobKey && activePhoto.detection && !activePhoto.detecting && (
                      <div className="rounded-md bg-primary/10 px-3 py-2 text-sm">
                        <div className="flex items-center gap-1.5 text-primary">
                          <SparklesIcon className="h-4 w-4" />
                          <span className="font-medium">AI detected: {activePhoto.detection.assetTypeName}</span>
                          {activePhoto.detection.confidence != null && (
                            <Badge variant="secondary" className="ml-1 text-xs">
                              {Math.round(activePhoto.detection.confidence * 100)}% confident
                            </Badge>
                          )}
                        </div>
                        {activePhoto.detection.problemType && (
                          <p className="mt-0.5 text-muted-foreground">Issue: {activePhoto.detection.problemType}</p>
                        )}
                      </div>
                    )}
                    {activePhoto.blobKey && !activePhoto.detection && !activePhoto.detecting && (
                      <p className="text-xs text-muted-foreground">AI analysis unavailable for this photo — you can still select a category manually.</p>
                    )}
                  </div>
                );
              })()
            )}

            {/* Empty state — no photos yet */}
            {photos.length === 0 && (
              <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-border">
                <CameraIcon className="h-12 w-12 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Take or upload photos</span>
                <div className="flex w-full max-w-xs flex-col gap-2 px-6">
                  <Button
                    type="button"
                    className="w-full"
                    onClick={() => multiPhoto.cameraInputRef.current?.click()}
                  >
                    <CameraIcon className="mr-2 h-4 w-4" />
                    Take a photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => multiPhoto.galleryInputRef.current?.click()}
                  >
                    <ImageIcon className="mr-2 h-4 w-4" />
                    Choose from gallery
                  </Button>
                </div>
              </div>
            )}

            {/* Add more photos (when at least one is attached but not at max) */}
            {photos.length > 0 && multiPhoto.canAddMore && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={isUploading}
                  onClick={() => multiPhoto.cameraInputRef.current?.click()}
                >
                  <CameraIcon className="mr-2 h-4 w-4" />
                  Take another
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={isUploading}
                  onClick={() => multiPhoto.galleryInputRef.current?.click()}
                >
                  <ImageIcon className="mr-2 h-4 w-4" />
                  Add from gallery
                </Button>
              </div>
            )}

            {/* Global upload error */}
            {photos.some((p) => p.error) && (
              <p className="text-sm text-destructive">Some photos failed to upload. Remove and try again.</p>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/ar')}
            >
              <SmartphoneIcon className="mr-2 h-4 w-4" />
              Open AR view
            </Button>
            <div className="space-y-2">
              <Button
                className="w-full"
                disabled={isUploading}
                onClick={() => goToStep('location')}
              >
                Continue
              </Button>
              {!hasUploadedPhoto && !isUploading && (
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={() => goToStep('location')}
                >
                  Continue without a photo
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ── Step: Location ── */}
        {step === 'location' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-medium">Location</h2>
              <p className="text-sm text-muted-foreground">
                Use GPS or drag the pin to the exact location.
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleGps}
              disabled={gpsLoading}
            >
              {gpsLoading ? (
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <NavigationIcon className="mr-2 h-4 w-4" />
              )}
              Use my current location
            </Button>
            {gpsError && (
              <p className="text-sm text-destructive">{gpsError}</p>
            )}
            {gpsPermissionDenied && !gpsError && (
              <Card className="border-amber-500/40 bg-amber-500/5">
                <CardContent className="space-y-3 py-3">
                  <div className="flex items-start gap-2">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="space-y-1.5 text-sm">
                      <p className="font-medium">Location access is blocked</p>
                      <p className="text-muted-foreground">
                        To use your current location, re-enable access for Safari on your iPhone:
                      </p>
                      <ol className="ml-4 list-decimal space-y-1 text-muted-foreground">
                        <li>Open <strong>Settings &rarr; Privacy &amp; Security &rarr; Location Services</strong> and make sure Location Services is on.</li>
                        <li>Scroll to <strong>Safari Websites</strong> and set it to <strong>Ask</strong> or <strong>While Using</strong>.</li>
                        <li>Or, in Safari, tap the <strong>aA</strong> menu &rarr; <strong>Website Settings &rarr; Location &rarr; Allow</strong>.</li>
                      </ol>
                      <p className="text-muted-foreground">
                        Then tap “Try again” below, or skip this and drop the pin on the map manually.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={handleGps}
                    disabled={gpsLoading}
                  >
                    {gpsLoading ? (
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <NavigationIcon className="mr-2 h-4 w-4" />
                    )}
                    Try again
                  </Button>
                </CardContent>
              </Card>
            )}
            <div className="h-64">
              <MapPinPicker
                latitude={latitude ?? undefined}
                longitude={longitude ?? undefined}
                onLocationChange={handleLocationChange}
                photoLocations={photoLocations}
              />
            </div>
            {latitude != null && longitude != null && (
              <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                <MapPinIcon className="mr-1.5 inline h-3.5 w-3.5" />
                {latitude.toFixed(5)}, {longitude.toFixed(5)}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="address">Address (optional)</Label>
              <AddressSearchInput
                id="address"
                value={addressText}
                onValueChange={setAddressText}
                onSelect={(result) => {
                  setAddressText(result.label);
                  handleLocationChange(result.latitude, result.longitude);
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={goBack} className="shrink-0">
                <ChevronLeftIcon className="h-4 w-4" />
                Back
              </Button>
              <Button
                className="flex-1"
                disabled={latitude == null || longitude == null}
                onClick={() => goToStep('category')}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* ── Step: Category ── */}
        {step === 'category' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-medium">What type of issue?</h2>
              <p className="text-sm text-muted-foreground">
                Showing types for <span className="font-medium text-foreground">{purposeLabel}</span>.
                Select the category that best describes the problem.
              </p>
            </div>
            {/* Purpose switcher — different lists per persona */}
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Report context">
              {REPORT_PURPOSES.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => setPurpose(p.slug)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                    purpose === p.slug
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {/* AI suggestion card (low-confidence or unmatched only) */}
            {detection?.categoryId &&
              (detection.confidence ?? 0) < 0.7 &&
              categories.some((c) => c.id === detection.categoryId) && (
              <Card className="border-primary/40 bg-primary/5">
                <CardContent className="space-y-2 py-3">
                  <div className="flex items-center gap-1.5">
                    <SparklesIcon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">AI Suggestion</span>
                    {detection.confidence != null && (
                      <Badge variant="secondary" className="text-xs">
                        {Math.round(detection.confidence * 100)}% confident
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm">
                    Detected <strong>{detection.assetTypeName}</strong> with issue:{' '}
                    <strong>{detection.problemType}</strong>
                  </p>
                  {detection.description && (
                    <p className="text-xs text-muted-foreground">{detection.description}</p>
                  )}
                  {matchedAsset && (
                    <div className="rounded-md bg-background/80 px-3 py-2 text-xs">
                      <span className="font-medium">Nearest known asset:</span>{' '}
                      {matchedAsset.assetTypeName} owned by{' '}
                      <strong>{matchedAsset.ownerTenantName}</strong>{' '}
                      <Badge variant="outline" className="ml-1 text-xs">
                        {matchedAsset.ownerTenantType === 'council' ? 'Council' : 'Service Provider'}
                      </Badge>
                      <span className="text-muted-foreground"> ({matchedAsset.distance}m away)</span>
                    </div>
                  )}
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setCategoryId(detection.categoryId ?? null);
                      setAiAutoSelected(false);
                      goToStep('deflection');
                    }}
                  >
                    <SparklesIcon className="mr-1.5 h-3.5 w-3.5" />
                    Use AI suggestion: {detection.categoryName}
                  </Button>
                </CardContent>
              </Card>
            )}
            {/* AI auto-selected notice */}
            {aiAutoSelected && categoryId && (
              <div className="flex items-center gap-1.5 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
                <SparklesIcon className="h-4 w-4" />
                <span className="font-medium">AI selected a category for you</span>
                <span className="text-muted-foreground">— tap another to change</span>
              </div>
            )}
            {/* Search */}
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search issue types..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {/* Category grid */}
            {filteredCategories.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {filteredCategories.map((cat: Category) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setCategoryId(cat.id ?? null);
                      setAiAutoSelected(false);
                      goToStep('deflection');
                    }}
                    className={cn(
                      'relative rounded-lg border p-3 text-left text-sm transition-colors hover:border-primary hover:bg-accent',
                      categoryId === cat.id && 'border-primary bg-accent'
                    )}
                  >
                    {aiAutoSelected && categoryId === cat.id && (
                      <Badge className="absolute right-1.5 top-1.5 gap-0.5 text-xs">
                        <SparklesIcon className="h-3 w-3" />
                        AI
                      </Badge>
                    )}
                    <span className="font-medium">{cat.name}</span>
                    {cat.assetTypeName && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {cat.assetTypeName}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-6 text-center text-sm text-muted-foreground">
                  No matching types — try a different search or add your own below.
                </CardContent>
              </Card>
            )}
            {/* Custom type entry */}
            <div className="space-y-2">
              {!showCustomInput ? (
                <button
                  onClick={() => setShowCustomInput(true)}
                  className="w-full rounded-lg border border-dashed border-primary/40 p-3 text-left text-sm text-primary transition-colors hover:bg-primary/5"
                >
                  <span className="font-medium">Can't find your issue type? Add your own</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Tell us what's wrong and we'll review it for the list
                  </span>
                </button>
              ) : (
                <Card className="border-primary/40">
                  <CardContent className="space-y-3 py-3">
                    <Label htmlFor="custom-type" className="text-sm font-medium">
                      Describe the issue type
                    </Label>
                    <Input
                      id="custom-type"
                      placeholder="e.g. Broken playground equipment"
                      value={customLabelInput}
                      maxLength={60}
                      onChange={(e) => {
                        setCustomLabelInput(e.target.value);
                        setCustomLabelError(null);
                      }}
                    />
                    {customLabelError && (
                      <p className="text-xs text-destructive">{customLabelError}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {customLabelInput.length}/60 characters
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          const trimmed = customLabelInput.trim();
                          if (trimmed.length < 3) {
                            setCustomLabelError('Please enter at least 3 characters.');
                            return;
                          }
                          if (trimmed.length > 60) {
                            setCustomLabelError('Label must be 60 characters or fewer.');
                            return;
                          }
                          const otherCat = categories.find((c) => c.slug === 'other');
                          if (!otherCat?.id) {
                            setCustomLabelError('Unable to set custom type. Please pick a category instead.');
                            return;
                          }
                          setCategoryId(otherCat.id);
                          setCustomTypeLabel(trimmed);
                          setAiAutoSelected(false);
                          goToStep('deflection');
                        }}
                      >
                        Use this type
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowCustomInput(false);
                          setCustomLabelInput('');
                          setCustomLabelError(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            {/* Continue button (visible when a category is selected) */}
            {categoryId && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={goBack} className="shrink-0">
                  <ChevronLeftIcon className="h-4 w-4" />
                  Back
                </Button>
                <Button className="flex-1" onClick={() => goToStep('deflection')}>
                  Continue
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Step: Deflection ── */}
        {step === 'deflection' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-medium">Similar reports nearby</h2>
              <p className="text-sm text-muted-foreground">
                Is your issue already listed? Add your support instead of creating a duplicate.
              </p>
            </div>
            {nearbyLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2Icon className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {!nearbyLoading && nearbyReports.length === 0 && (
              <Card>
                <CardContent className="py-6 text-center text-sm text-muted-foreground">
                  No similar reports found nearby. Your report will be new.
                </CardContent>
              </Card>
            )}
            {nearbyReports.map((report: NearbyReport) => {
              const cfg = getStatusConfig(report.status);
              return (
                <Card key={report.id}>
                  <CardContent className="space-y-3 py-3">
                    <div className="flex gap-3">
                      {report.photoUrl && (
                        <img
                          src={report.photoUrl}
                          alt=""
                          className="h-16 w-16 rounded-md object-cover"
                        />
                      )}
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                          {report.customTypeLabel
                            ? `Other — ${report.customTypeLabel}`
                            : report.categoryName}
                        </span>
                          <Badge variant="secondary" className={cn('text-xs', cfg.badgeClass)}>
                            {cfg.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatDistance(report.distance)} · {formatRelativeTime(report.createdAt)}
                        </p>
                        {report.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {report.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {report.upvoteCount ?? 0} support{report.upvoteCount === 1 ? '' : 's'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={upvoteMutation.isPending}
                      onClick={() => report.trackingToken && handleUpvote(report.trackingToken)}
                    >
                      <ThumbsUpIcon className="mr-2 h-4 w-4" />
                      This is my issue — add my support
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            {nearbyReports.length > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={goBack} className="shrink-0">
                  <ChevronLeftIcon className="h-4 w-4" />
                  Back
                </Button>
                <Button variant="ghost" className="flex-1" onClick={() => goToStep('details')}>
                  None of these match — submit my report
                </Button>
              </div>
            )}
            {nearbyReports.length === 0 && !nearbyLoading && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={goBack} className="shrink-0">
                  <ChevronLeftIcon className="h-4 w-4" />
                  Back
                </Button>
                <Button className="flex-1" onClick={() => goToStep('details')}>
                  Continue
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Step: Details ── */}
        {step === 'details' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-medium">Additional details</h2>
              <p className="text-sm text-muted-foreground">
                Add any extra information that might help. All fields are optional.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe the issue in more detail..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="severity">
                Severity
                {detection?.severity && (
                  <span className="ml-2 text-xs font-normal text-primary">
                    <SparklesIcon className="mr-1 inline h-3 w-3" />
                    AI-suggested
                  </span>
                )}
              </Label>
              <div className="flex gap-2">
                {(['low', 'medium', 'high', 'urgent'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSeverity(s)}
                    className={cn(
                      'flex-1 rounded-md border px-3 py-2 text-sm transition-colors',
                      severity === s ? 'border-primary bg-accent font-medium' : 'border-border'
                    )}
                  >
                    {SEVERITY_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Your name (optional)</Label>
              <Input
                id="name"
                placeholder="John Citizen"
                value={reporterName}
                onChange={(e) => setReporterName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email (optional)</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={reporterEmail}
                onChange={(e) => setReporterEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="0412 345 678"
                value={reporterPhone}
                onChange={(e) => setReporterPhone(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium">Keep my contact details private</p>
                <p className="text-xs text-muted-foreground">
                  If on, only the responsible authority can see your details.
                </p>
              </div>
              <Switch checked={contactPrivacy} onCheckedChange={setContactPrivacy} />
            </div>
            {submitError && (
              <p className="text-sm text-destructive">{submitError}</p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={goBack} className="shrink-0" disabled={createMutation.isPending}>
                <ChevronLeftIcon className="h-4 w-4" />
                Back
              </Button>
              <Button
                className="flex-1"
                disabled={createMutation.isPending}
                onClick={handleSubmit}
              >
                {createMutation.isPending && <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />}
                Submit Report
              </Button>
            </div>
          </div>
        )}

        {/* ── Step: Success ── */}
        {step === 'success' && (
          <div className="space-y-6 py-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-950">
              <CheckCircle2Icon className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Report submitted!</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Your report has been sent to the responsible authority. Save this private tracking link to check its status later.
              </p>
            </div>

            {/* Report attached at submission (signed-in user) */}
            {attachedAtSubmit && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-left dark:border-green-900 dark:bg-green-950">
                <div className="flex items-center gap-2">
                  <UserCheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">
                    This report is attached to your account
                  </span>
                </div>
                <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                  You can track and interact with this report from My Reports.
                </p>
                <Button asChild size="sm" variant="outline" className="mt-2">
                  <Link to="/my-reports">View My Reports</Link>
                </Button>
              </div>
            )}

            {/* Sign-in prompt for anonymous reporters */}
            {!attachedAtSubmit && authEnabled && claimStatus === 'idle' && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-left">
                <div className="flex items-center gap-2">
                  <LogInIcon className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">Sign in to see and interact with your report</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Sign in with your work account to track this report from your account, add updates, and upvote — without needing to keep the tracking link.
                </p>
                <Button size="sm" className="mt-2" onClick={handleSignInForClaim}>
                  <LogInIcon className="mr-2 h-4 w-4" />
                  Sign in with work account
                </Button>
              </div>
            )}

            {/* Claim in progress (after sign-in redirect) */}
            {!attachedAtSubmit && (claimStatus === 'pending-claim' || claimStatus === 'claiming') && (
              <div className="flex items-center justify-center gap-2 rounded-lg border bg-muted px-4 py-3 text-sm text-muted-foreground">
                <Loader2Icon className="h-4 w-4 animate-spin" />
                {claimStatus === 'pending-claim' ? 'Completing sign-in…' : 'Attaching report to your account…'}
              </div>
            )}

            {/* Claim succeeded */}
            {!attachedAtSubmit && (claimStatus === 'claimed' || claimStatus === 'already-owned') && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-left dark:border-green-900 dark:bg-green-950">
                <div className="flex items-center gap-2">
                  <UserCheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">
                    Report attached to your account
                  </span>
                </div>
                <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                  You can now track and interact with this report from My Reports.
                </p>
                <Button asChild size="sm" variant="outline" className="mt-2">
                  <Link to="/my-reports">View My Reports</Link>
                </Button>
              </div>
            )}

            {/* Claim failed — graceful fallback */}
            {!attachedAtSubmit && claimStatus === 'error' && (
              <p className="text-xs text-muted-foreground">
                Couldn't attach the report to your account — you can still track it with the link below.
              </p>
            )}

            {trackingUrl && (
              <div className="space-y-2 text-left">
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                  <p className="text-sm font-medium">Your private report link</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Save or copy this link now. It is the direct way to return to this report’s status page.
                  </p>
                  {trackingToken && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Report reference: <span className="font-mono text-foreground">{trackingToken}</span>
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    ref={trackingLinkInputRef}
                    value={new URL(trackingUrl, window.location.origin).toString()}
                    readOnly
                    className="text-sm"
                    aria-label="Private report tracking link"
                    onFocus={(event) => event.currentTarget.select()}
                  />
                  <Button variant="outline" onClick={handleCopyLink} aria-label="Copy tracking link">
                    {copied ? <CheckCircle2Icon className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {trackingUrl && (
                <Button asChild>
                  <Link to={trackingUrl}>View Report Status</Link>
                </Button>
              )}
              <Button variant="ghost" asChild>
                <Link to={`/report?purpose=${purpose}`}>Report Another Issue</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Leave-report confirmation */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="max-w-sm" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Leave this report?</DialogTitle>
            <DialogDescription>
              Your progress will be lost. You’ll need to start a new report next time.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>
              Stay
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowLeaveDialog(false);
                navigate('/');
              }}
            >
              Leave
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
